// ============================================================
// CommsQuantumBackdrop — the reusable "Quantum Comms Cockpit" backdrop
// (PHASE_2M D2). A faint energy field behind the comms surfaces: layered
// radial color fields (cyan / violet / magenta), two slow-drifting orbital
// rings, and a sparse SVG particle field. CSS + inline SVG only — no images,
// no canvas. pointer-events: none; very low opacity so text stays readable;
// slow, non-distracting animation; zero layout shift (absolute inset 0).
// ============================================================

export const QUANTUM_BACKDROP_MARKER = 'sqv2-quantum-backdrop'

// Deterministic particle positions (no Math.random — SSR-stable markup).
const PARTICLES: Array<[number, number, number, number]> = [
  // [x%, y%, radius, durationSeconds]
  [8, 18, 1.4, 9], [16, 64, 1.0, 12], [24, 32, 1.2, 10], [33, 78, 0.9, 14],
  [41, 12, 1.3, 11], [52, 56, 1.0, 13], [61, 26, 1.5, 9], [69, 70, 0.9, 15],
  [77, 40, 1.2, 10], [85, 82, 1.0, 12], [91, 22, 1.3, 11], [46, 90, 1.1, 13],
]

export default function CommsQuantumBackdrop() {
  return (
    <div
      className={QUANTUM_BACKDROP_MARKER}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}
    >
      <style>{`
        @keyframes sqv2QuantumDrift { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes sqv2QuantumBreathe { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          .${QUANTUM_BACKDROP_MARKER} * { animation: none !important; }
        }
      `}</style>
      {/* soft color fields — the "dark but not dead" base */}
      <div style={{
        position: 'absolute', inset: 0,
        background: [
          'radial-gradient(ellipse 60% 45% at 12% 0%, rgba(8,145,178,0.07), transparent 70%)',
          'radial-gradient(ellipse 55% 50% at 88% 8%, rgba(124,58,237,0.06), transparent 70%)',
          'radial-gradient(ellipse 70% 55% at 50% 105%, rgba(201,123,173,0.05), transparent 72%)',
        ].join(', '),
      }} />
      {/* orbital rings — slow drift, barely there */}
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }} preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 800">
        <g style={{ transformOrigin: '600px 400px', animation: 'sqv2QuantumDrift 240s linear infinite' }}>
          <ellipse cx="600" cy="400" rx="520" ry="300" fill="none" stroke="rgba(8,145,178,0.05)" strokeWidth="1" strokeDasharray="3 14" />
          <ellipse cx="600" cy="400" rx="380" ry="210" fill="none" stroke="rgba(124,58,237,0.05)" strokeWidth="1" strokeDasharray="2 18" />
        </g>
        {PARTICLES.map(([x, y, r, dur], i) => (
          <circle
            key={i}
            cx={(x / 100) * 1200}
            cy={(y / 100) * 800}
            r={r}
            fill={i % 3 === 0 ? 'rgba(8,145,178,0.18)' : i % 3 === 1 ? 'rgba(124,58,237,0.16)' : 'rgba(201,123,173,0.14)'}
            style={{ animation: `sqv2QuantumBreathe ${dur}s ease-in-out infinite` }}
          />
        ))}
      </svg>
    </div>
  )
}
